import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from './shared/RichTextContent';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * C26 Hub Flyer — the official-partnership flyer image followed by a
 * primary-color CTA band. The band mirrors the mockup: a dark top strip,
 * centered contact copy, two outlined CTAs (Visit C26 Hub / Contact Us),
 * and a diamond divider underscoring the section.
 *
 * The flyer image slot has no hardcoded default — operators pick the
 * current flyer from the property media library in the editor.
 */

// ─── Slot group definitions (static literals for schema extraction) ───────

const flyerImageDef: SlotGroupDefinition = {
  label: 'C26 Flyer Image',
  description:
    'The current C26 Hub partnership flyer. Pick the uploaded flyer from the media library — leave the image blank and the section hides on the live site.',
  fields: {
    image: {
      label: 'Flyer Image',
      type: 'image',
      defaultValue: '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'C26 Hub Gym and Pool resident access flyer',
    },
  },
};

const visitCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA (Visit C26 Hub)',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.c26hub.com/',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Visit C26 Hub',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA (Contact Us)',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// ─── Component ────────────────────────────────────────────────────────────

export function C26HubFlyer() {
  const flyer = useSlotGroup('c26hubflyer.flyer_image', flyerImageDef);
  const visitCta = useSlotGroup('c26hubflyer.visit_cta', visitCtaDef);
  const contactCta = useSlotGroup('c26hubflyer.contact_cta', contactCtaDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const visitTarget = visitCta._target || '_blank';
  const contactTarget = contactCta._target || '_self';
  const visitRel = visitTarget === '_blank' ? 'noopener noreferrer' : undefined;
  const contactRel = contactTarget === '_blank' ? 'noopener noreferrer' : undefined;

  const hasFlyer = Boolean(flyer.image && flyer.image.trim());

  return (
    <section ref={sectionRef} className="reveal-fade-up c26-flyer">
      {/* ── Flyer image ── */}
      <div className="c26-flyer__media-wrap">
        <EditableSlotGroup
          groupPath="c26hubflyer.flyer_image"
          definition={flyerImageDef}
          className="c26-flyer__media"
        >
          {hasFlyer ? (
            <VibesitesImage
              url={flyer.image || ''}
              alt={flyer.alt || 'C26 Hub Gym and Pool resident access flyer'}
              className="c26-flyer__img"
              sizes="(max-width: 1024px) 100vw, 1024px"
            />
          ) : (
            <div className="c26-flyer__placeholder">
              <p>Pick the C26 flyer image from your media library</p>
            </div>
          )}
        </EditableSlotGroup>
      </div>

      {/* ── CTA band ── */}
      <div className="c26-flyer__cta-band">
        <div className="c26-flyer__band-inner">
          <RichTextContent
            path="c26hubflyer.band_heading"
            label="Band Heading"
            defaultValue="<p>For questions, contact C26 directly. For residency verification or Hawkflock card assistance, contact The Residences at Bluhawk leasing office.</p>"
            as="div"
            className="c26-flyer__band-heading"
          />

          <div className="c26-flyer__band-ctas">
            <EditableSlotGroup groupPath="c26hubflyer.visit_cta" definition={visitCtaDef}>
              <a
                href={visitCta.url || 'https://www.c26hub.com/'}
                target={visitTarget}
                rel={visitRel}
                className="c26-flyer__cta-btn"
              >
                <span className="c26-flyer__cta-label">{visitCta.label || 'Visit C26 Hub'}</span>
              </a>
            </EditableSlotGroup>

            <EditableSlotGroup groupPath="c26hubflyer.contact_cta" definition={contactCtaDef}>
              <a
                href={contactCta.url || '/contact-us'}
                target={contactTarget}
                rel={contactRel}
                className="c26-flyer__cta-btn"
              >
                <span className="c26-flyer__cta-label">{contactCta.label || 'Contact Us'}</span>
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* The flyer section flows directly into the CTA band — no body-color
           background between them so the two feel like one connected block. */
        .c26-flyer {
          background: var(--vibe-color-primary, #5a7a8c);
          color: var(--vibe-color-text-on-primary, #ffffff);
          padding: 2.5rem 0 0;
        }
        @media (min-width: 768px) {
          .c26-flyer { padding: 3.5rem 0 0; }
        }

        /* ── Flyer image ── */
        .c26-flyer__media-wrap {
          max-width: 64rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .c26-flyer__media-wrap { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .c26-flyer__media-wrap { padding: 0 2rem; } }

        .c26-flyer__media {
          display: block;
          position: relative;
          width: 100%;
          border-radius: var(--vibe-radius-lg, 14px);
          overflow: hidden;
          box-shadow: 0 18px 44px rgba(0, 0, 0, 0.28);
          background: #f6f6f8;
        }

        .c26-flyer__img {
          display: block;
          width: 100%;
          height: auto;
          object-fit: contain;
        }

        .c26-flyer__placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 260px;
          padding: 2rem;
          background:
            repeating-linear-gradient(
              135deg,
              #f1f1f3,
              #f1f1f3 12px,
              #ececef 12px,
              #ececef 24px
            );
          color: var(--vibe-color-text-muted);
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          text-align: center;
        }
        .c26-flyer__placeholder p { margin: 0; }

        /* ── CTA band — same primary surface, just below the flyer ── */
        .c26-flyer__cta-band {
          padding: 2.25rem 1rem 2rem;
        }
        @media (min-width: 768px) {
          .c26-flyer__cta-band { padding: 2.75rem 1.5rem 2.25rem; }
        }

        .c26-flyer__band-inner {
          max-width: 64rem;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
          text-align: center;
        }
        @media (min-width: 768px) {
          .c26-flyer__band-inner { gap: 1.75rem; }
        }

        .c26-flyer__band-heading {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.65;
          margin: 0;
          color: inherit;
          max-width: 52rem;
          opacity: 0.92;
        }
        @media (min-width: 768px) {
          .c26-flyer__band-heading { font-size: 1.0625rem; }
        }
        .c26-flyer__band-heading p { margin: 0; }

        .c26-flyer__band-ctas {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.875rem;
          width: 100%;
        }
        @media (min-width: 640px) {
          .c26-flyer__band-ctas { flex-direction: row; gap: 1rem; }
        }

        /* ── CTA button — mirrors the Price Brothers .hero-solid-cta-btn ── */
        /* Resting: 1.5px / 75%-opacity white border, transparent fill, uppercase
           tracking-wider heading font, 2px radius. Hover fills with the brand's
           secondary color and flips the label to text-on-secondary — same
           treatment as every inner-page hero CTA on the site. */
        .c26-flyer__cta-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 2rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.875rem;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          text-decoration: none;
          color: var(--vibe-color-text-on-primary, #ffffff);
          background: transparent;
          border: 1.5px solid color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 75%, transparent);
          border-radius: 2px;
          overflow: hidden;
          transition: border-color 0.25s ease, color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
        }
        .c26-flyer__cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vibe-color-secondary);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .c26-flyer__cta-btn:hover {
          border-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 25%, transparent);
        }
        .c26-flyer__cta-btn:hover::before { opacity: 1; }
        .c26-flyer__cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
        .c26-flyer__cta-label { position: relative; z-index: 1; }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'C26HubFlyer',
  displayName: 'C26 Hub — Flyer & CTA Band',
  tokens: {
    'c26hubflyer.band_heading': {
      label: 'CTA Band Heading',
      type: 'richtext',
      defaultValue:
        '<p>For questions, contact C26 directly. For residency verification or Hawkflock card assistance, contact The Residences at Bluhawk leasing office.</p>',
    },
  },
  slotGroups: {
    'c26hubflyer.flyer_image': flyerImageDef,
    'c26hubflyer.visit_cta': visitCtaDef,
    'c26hubflyer.contact_cta': contactCtaDef,
  },
};

(C26HubFlyer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default C26HubFlyer;
